
import { Link } from 'react-router-dom';

export default function Learnings() {
  const learnings = [
    {
      title: 'Localization is More Than Translation',
      description:
        'Building for USA and India required fundamentally different UX patterns, not just language changes. Payment flows, communication preferences, and trust signals vary dramatically.',
      icon: 'ri-global-line',
      color: '#1FA372',
      bgColor: '#F0FDF9',
    },
    {
      title: 'Simplicity Requires Deep Understanding',
      description:
        'Making complex workflows simple required extensive research and iteration. The final 3-step payment flow went through 12 iterations before achieving optimal conversion.',
      icon: 'ri-lightbulb-line',
      color: '#F97316',
      bgColor: '#FFF7ED',
    },
    {
      title: 'Role-Based Design is Essential',
      description:
        'A one-size-fits-all approach fails in enterprise software. Each user type needs a tailored experience that respects their unique context and goals.',
      icon: 'ri-user-settings-line',
      color: '#3B82F6',
      bgColor: '#EFF6FF',
    },
    {
      title: 'Real-Time Feedback Builds Trust',
      description:
        "Transparency through real-time status updates dramatically reduced support inquiries and increased user satisfaction. Users don't mind waiting if they know what's happening.",
      icon: 'ri-refresh-line',
      color: '#8B5CF6',
      bgColor: '#F5F3FF',
    },
  ];

  const reflections = [
    'Collaborated with 40+ stakeholders across property management, legal, and finance',
    'Conducted 3 rounds of usability testing with 60+ participants',
    'Iterated through 200+ design variations across key flows',
    'Reduced design-to-development handoff time by 60% with detailed specs',
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-full text-sm font-semibold mb-4 border border-[#8B5CF6]/20">
            10 — Reflections
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Learnings &amp; Reflections
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Key insights gained from designing a complex enterprise platform for
            diverse global markets.
          </p>
        </div>

        {/* Key Learnings */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {learnings.map((learning, index) => (
            <div
              key={index}
              className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6 border"
                style={{ backgroundColor: learning.bgColor, borderColor: `${learning.color}30` }}
              >
                <i
                  className={`${learning.icon} text-2xl`}
                  style={{ color: learning.color }}
                ></i>
              </div>
              <h3 className="text-xl font-bold text-[#1E293B] mb-3">{learning.title}</h3>
              <p className="text-[#64748B] leading-relaxed">{learning.description}</p>
            </div>
          ))}
        </div>

        {/* Process Reflections */}
        <div className="bg-gradient-to-r from-[#F0FDF9] via-white to-[#FFF7ED] rounded-3xl p-8 border border-[#E2E8F0] mb-16 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">
            Process Highlights
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            {reflections.map((reflection, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-white rounded-xl p-4 border border-[#E2E8F0] shadow-sm"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#1FA372]/10 rounded-lg flex-shrink-0 border border-[#1FA372]/20">
                  <i className="ri-check-double-line text-[#1FA372]"></i>
                </div>
                <p className="text-[#64748B]">{reflection}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] mb-16 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-6 text-center">
            What&apos;s Next
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center border border-[#E2E8F0] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#1FA372]/10 rounded-xl mx-auto mb-4 border border-[#1FA372]/20">
                <i className="ri-robot-line text-[#1FA372] text-xl"></i>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">AI Assistant</h4>
              <p className="text-[#64748B] text-sm">
                Intelligent chatbot for instant resident support
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center border border-[#E2E8F0] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#F97316]/10 rounded-xl mx-auto mb-4 border border-[#F97316]/20">
                <i className="ri-home-wifi-line text-[#F97316] text-xl"></i>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">
                Smart Home Integration
              </h4>
              <p className="text-[#64748B] text-sm">
                Connect with IoT devices for automated access
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center border border-[#E2E8F0] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#3B82F6]/10 rounded-xl mx-auto mb-4 border border-[#3B82F6]/20">
                <i className="ri-bar-chart-box-line text-[#3B82F6] text-xl"></i>
              </div>
              <h4 className="text-[#1E293B] font-semibold mb-2">
                Predictive Analytics
              </h4>
              <p className="text-[#64748B] text-sm">
                AI-powered insights for proactive management
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-[#1FA372] to-[#14B8A6] rounded-3xl p-12 relative overflow-hidden shadow-xl">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-24 h-24 border-2 border-white rounded-full"></div>
              <div className="absolute top-1/2 left-1/4 w-16 h-16 border-2 border-white rounded-full"></div>
            </div>

            <div className="relative">
              <h3 className="text-3xl font-bold text-white mb-4">
                Interested in Working Together?
              </h3>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                I&apos;m always excited to discuss new projects and opportunities in
                product design.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/#contact"
                  className="px-8 py-3 bg-white text-[#1FA372] font-semibold rounded-full hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap shadow-md"
                >
                  Get in Touch
                </Link>
                <Link
                  to="/"
                  className="px-8 py-3 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View More Projects
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
            <p className="text-[#64748B] text-sm">
              © {new Date().getFullYear()} Rangababu Budireddy. All rights
              reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#94A3B8] hover:text-[#1FA372] text-sm mt-2 inline-block transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
